import { useState } from "react";
import { Quote, AlertTriangle, ListOrdered, Lightbulb, CheckCircle2, MinusCircle, XCircle, Copy, Check, BookMarked } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { EssayReview } from "@/lib/smartessay.functions";

type Tab = "evidence" | "flow" | "ideas" | "formatting";

const PRIORITY_COLOR: Record<string, string> = {
  high: "border-destructive/40 bg-destructive/5 text-destructive",
  medium: "border-sun/40 bg-sun/10 text-foreground",
  low: "border-border bg-muted text-muted-foreground",
};

const SEVERITY_COLOR: Record<string, string> = {
  high: "border-destructive/40 bg-destructive/5",
  medium: "border-sun/40 bg-sun/10",
  low: "border-border bg-muted",
};

const FLOW_ICON = {
  strong: <CheckCircle2 className="h-4 w-4 text-mint" />,
  ok: <MinusCircle className="h-4 w-4 text-sun" />,
  weak: <XCircle className="h-4 w-4 text-destructive" />,
};

export function EssayReviewView({ review }: { review: EssayReview }) {
  const [tab, setTab] = useState<Tab>("evidence");
  const [copied, setCopied] = useState<string | null>(null);

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
  }

  return (
    <div className="space-y-6">
      {/* Overall summary card */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-semibold">Review summary</h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground leading-relaxed">
              {review.overall_summary}
            </p>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-border bg-background px-5 py-3">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Score</span>
            <span className="font-display text-3xl font-semibold text-primary">{review.overall_score}</span>
            <span className="text-xs text-muted-foreground">/ 100</span>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Thesis</p>
          <p className="mt-1 text-sm">{review.thesis_assessment}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 rounded-lg bg-muted p-1">
        <TabButton active={tab === "evidence"} onClick={() => setTab("evidence")} icon={<Quote className="h-4 w-4" />}>
          Evidence gaps
          <Badge>{review.evidence_gaps.length}</Badge>
        </TabButton>
        <TabButton active={tab === "flow"} onClick={() => setTab("flow")} icon={<ListOrdered className="h-4 w-4" />}>
          Logical flow
          <Badge>{review.logical_flow.flow_issues.length}</Badge>
        </TabButton>
        <TabButton active={tab === "ideas"} onClick={() => setTab("ideas")} icon={<Lightbulb className="h-4 w-4" />}>
          Improvement ideas
          <Badge>{review.improvement_ideas.length}</Badge>
        </TabButton>
        {review.formatting_check && (
          <TabButton active={tab === "formatting"} onClick={() => setTab("formatting")} icon={<BookMarked className="h-4 w-4" />}>
            {review.formatting_check.style} formatting
            <Badge>{review.formatting_check.issues.length}</Badge>
          </TabButton>
        )}
      </div>

      {/* Tab content */}
      {tab === "evidence" && (
        <div className="space-y-4">
          {review.evidence_gaps.length === 0 ? (
            <EmptyState icon={<CheckCircle2 className="h-6 w-6 text-mint" />} title="No evidence gaps found" body="Your claims look well-supported. Nice work." />
          ) : (
            review.evidence_gaps.map((gap, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Paragraph {gap.paragraph_index}
                </div>
                <blockquote className="mt-2 border-l-2 border-border pl-3 text-sm italic text-muted-foreground">
                  "{gap.excerpt}"
                </blockquote>
                <p className="mt-3 text-sm"><span className="font-medium">Issue:</span> {gap.issue}</p>
                {gap.suggested_quotes.length > 0 && (
                  <div className="mt-5 space-y-4 rounded-xl border-2 border-primary/30 bg-primary/[0.07] p-5">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15">
                        <Quote className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <p className="text-sm font-semibold uppercase tracking-wider text-primary">Suggested quotes from your source</p>
                    </div>
                    {gap.suggested_quotes.map((q, j) => {
                      const key = `${i}-${j}`;
                      const fullCite = `"${q.quote}" ${q.citation}`;
                      return (
                        <div key={j} className="rounded-xl border border-primary/25 bg-background p-4 shadow-soft">
                          <blockquote className="border-l-3 border-primary pl-3 text-sm italic leading-relaxed">
                            "{q.quote}" <span className="not-italic text-muted-foreground">{q.citation}</span>
                          </blockquote>
                          <p className="mt-2 text-xs text-muted-foreground">{q.why_it_fits}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-3"
                            onClick={() => copy(fullCite, key)}
                          >
                            {copied === key ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
                            {copied === key ? "Copied" : "Copy quote + citation"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === "flow" && (
        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Overall flow</p>
            <p className="mt-2 text-sm">{review.logical_flow.overall}</p>
          </div>

          <div>
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Paragraph map</h3>
            <ol className="space-y-2">
              {review.logical_flow.paragraph_map.map((p) => (
                <li key={p.paragraph_index} className="rounded-xl border border-border bg-card p-4 shadow-soft">
                  <div className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {p.paragraph_index}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{p.role}</span>
                        <span className="flex items-center gap-1 text-xs">
                          {FLOW_ICON[p.flow_score]}
                          <span className="capitalize">{p.flow_score}</span>
                        </span>
                      </div>
                      <p className="mt-1 text-sm font-medium">{p.main_idea}</p>
                      <p className="mt-1 text-xs text-muted-foreground"><span className="font-medium text-foreground">Transition:</span> {p.transition_in}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {review.logical_flow.flow_issues.length > 0 && (
            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Flow issues</h3>
              <div className="space-y-3">
                {review.logical_flow.flow_issues.map((issue, i) => (
                  <div key={i} className="rounded-xl border border-sun/30 bg-sun/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                      <AlertTriangle className="h-3.5 w-3.5 text-sun" />
                      Between paragraphs {issue.between_paragraphs}
                    </div>
                    <p className="mt-2 text-sm"><span className="font-medium">Problem:</span> {issue.problem}</p>
                    <p className="mt-1 text-sm"><span className="font-medium">Fix:</span> {issue.fix}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "ideas" && (
        <div className="space-y-3">
          {review.improvement_ideas.length === 0 ? (
            <EmptyState icon={<CheckCircle2 className="h-6 w-6 text-mint" />} title="No improvement ideas" body="Your essay is in great shape." />
          ) : (
            [...review.improvement_ideas]
              .sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority))
              .map((idea, i) => (
                <div key={i} className={`rounded-xl border p-4 ${PRIORITY_COLOR[idea.priority] ?? "border-border bg-card"}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                    <span>{idea.priority} priority</span>
                    <span className="opacity-60">·</span>
                    <span>{idea.category}</span>
                  </div>
                  <p className="mt-2 text-sm font-medium text-foreground">{idea.suggestion}</p>
                  {idea.example && (
                    <p className="mt-2 rounded-md border border-border bg-background/60 p-3 text-xs italic text-muted-foreground">
                      Example: {idea.example}
                    </p>
                  )}
                </div>
              ))
          )}
        </div>
      )}

      {tab === "formatting" && review.formatting_check && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {review.formatting_check.style} formatting
                </p>
                <p className="mt-2 text-sm">{review.formatting_check.overall}</p>
              </div>
              <div className="flex flex-col items-center rounded-xl border border-border bg-background px-4 py-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Score</span>
                <span className="font-display text-2xl font-semibold text-primary">
                  {review.formatting_check.score}
                </span>
                <span className="text-[10px] text-muted-foreground">/ 100</span>
              </div>
            </div>
          </div>

          {review.formatting_check.strengths.length > 0 && (
            <div className="rounded-2xl border border-mint/30 bg-mint/5 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground">What you're getting right</p>
              <ul className="mt-3 space-y-2">
                {review.formatting_check.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {review.formatting_check.issues.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-6 w-6 text-mint" />}
              title={`No ${review.formatting_check.style} formatting issues found`}
              body="Your citations and formatting follow the style conventions."
            />
          ) : (
            <div className="space-y-3">
              {[...review.formatting_check.issues]
                .sort((a, b) => priorityRank(a.severity) - priorityRank(b.severity))
                .map((issue, i) => (
                  <div key={i} className={`rounded-xl border p-4 ${SEVERITY_COLOR[issue.severity] ?? "border-border bg-card"}`}>
                    <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>{issue.severity} severity</span>
                      <span className="opacity-60">·</span>
                      <span>{issue.category}</span>
                    </div>
                    <p className="mt-2 text-sm"><span className="font-medium">Issue:</span> {issue.description}</p>
                    <p className="mt-1 text-sm"><span className="font-medium">Fix:</span> {issue.fix}</p>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function priorityRank(p: "high" | "medium" | "low") {
  return p === "high" ? 0 : p === "medium" ? 1 : 2;
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
        active ? "bg-card shadow-soft text-foreground" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
      <span className="sm:hidden">{Array.isArray(children) ? children[0] : children}</span>
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="ml-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{children}</span>;
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">{icon}</div>
      <h3 className="mt-3 font-display text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
